import React from "react";
import toastr from "toastr";
import { KTMScreen } from "ktm-ui";
import { Spinner, Table } from "react-bootstrap";
import axios from "axios";
import CustomPagination from "components/CustomPagination";
import EditPlanAdminSettingModal from "components/EditPlanAdminSettingModal";
import ConfirmActionModal from "components/ConfirmActionModal";
import PlanAdminLogs from "./PlanAdmin_log";
import { paTypeOf } from "./pa-setting-helpers";
import moment from "moment";
import "../../styles/CustomComponents.css";

type PaColumn = {
  label: string;
  key: string;
  flag?: boolean;
  className?: string;
};

const PA_TYPE_LABELS: { [key: string]: string } = {
  group: "Group",
  individual: "Individual",
};

export default KTMScreen(
  { name: "assign_planadmin" },
  class extends React.Component<any, any> {

    constructor(props: any) {
      super(props);
      this.state = {
        loading: true,
        planadmins: [],
        adminEmailInput: "",
        currentPage: 1,
        itemsPerPage: 8,
        isPAUpdated: false,
        logRefreshKey: 0,
        pendingAction: null,
      };

      this.getPlanAdmins = this.getPlanAdmins.bind(this);
      this.assignAdmin = this.assignAdmin.bind(this);
      this.removeAdmin = this.removeAdmin.bind(this);
    }

    componentDidMount() {
     this.getPlanAdmins();
    }

    componentDidUpdate(prevProps: any, prevState: any) {
      if (this.state.isPAUpdated !== prevState.isPAUpdated) {
        this.getPlanAdmins();
        if (this.state.isPAUpdated) {
          this.refreshLogs();
        }
        this.setState({ isPAUpdated: false });
      }
    }

    refreshLogs = () => {
      this.setState((prevState: any) => ({
        logRefreshKey: prevState.logRefreshKey + 1,
      }));
    };

    setCurrentPage = (page: number) => {
      this.setState({ currentPage: page });
    };

    renderFlag = (row: any, field: string, label: string) => {
      const on = !!row[field];
      const text = `${label}: ${on ? "Yes" : "No"}`;

      return (
        <span
          className={`pa-flag pa-flag--${on ? "yes" : "no"}`}
          title={text}
          aria-label={text}
        >
          {on ? "\u2713" : "\u2013"}
        </span>
      );
    };

    renderPaType = (row: any) => PA_TYPE_LABELS[paTypeOf(row)] || null;

    renderPermissions = (row: any) => {
      if (!row.readOnly && !row.showCustomCatReport) {
        return null;
      }

      return (
        <div className="pa-pills">
          {row.readOnly && <span className="pa-pill pa-pill--readonly">Read-only</span>}
          {row.showCustomCatReport && (
            <span className="pa-pill pa-pill--report">Enhanced Benefit Report</span>
          )}
        </div>
      );
    };

    requestAction = (type: "remove" | "send", row: any) => {
      this.setState({ pendingAction: { type, admin: row } });
    };

    cancelPendingAction = () => {
      this.setState({ pendingAction: null });
    };

    confirmPendingAction = () => {
      const pending = this.state.pendingAction;
      this.setState({ pendingAction: null });
      if (!pending) {
        return;
      }
      if (pending.type === "remove") {
        this.removeAdmin(pending.admin.id);
      } else {
        this.sendEmail(pending.admin.id);
      }
    };

    describeAdmin = (row: any) => {
      const name = `${row.firstName || ""} ${row.lastName || ""}`.trim();
      return name ? `${name} (${row.email})` : row.email;
    };

    renderConfirmModal = () => {
      const pending = this.state.pendingAction;
      if (!pending) {
        return null;
      }

      const who = this.describeAdmin(pending.admin);
      const isRemove = pending.type === "remove";

      return (
        <ConfirmActionModal
          title={isRemove ? "Remove plan admin" : "Send plan admin email"}
          message={
            isRemove
              ? `Remove ${who} as a plan admin for this policy?`
              : `Send the plan admin email to ${who}?`
          }
          confirmLabel={isRemove ? "Remove" : "Send Email"}
          danger={isRemove}
          onConfirm={this.confirmPendingAction}
          onCancel={this.cancelPendingAction}
        />
      );
    };

    renderActions = (row: any) => (
      <>
        <EditPlanAdminSettingModal
          policyId={this.props.policy_id}
          paData={row}
          updatePA={() => {
            this.setState({
              isPAUpdated: true,
            });
          }}
        />
        <span className="pa-action-sep">|</span>
        <a href="#"
          className="RT-Link"
          onClick={(e) => { e.preventDefault(); this.requestAction("send", row);}}
        >
          Send Email
        </a>
        <span className="pa-action-sep">|</span>
        <a href="#"
          className="RT-Link"
          onClick={(e) => { e.preventDefault(); this.requestAction("remove", row);}}
          style={{ color: "red" }}
        >
          Remove
        </a>
      </>
    );

    renderCell = (column: PaColumn, row: any) => {
      if (column.flag) {
        return this.renderFlag(row, column.key, column.label);
      }

      switch (column.key) {
        case "email":
          return <span className="pa-email" title={row.email}>{row.email}</span>;
        case "paType":
          return this.renderPaType(row);
        case "permissions":
          return this.renderPermissions(row);
        case "role":
          return row.role == null ? "" : row.role;
        case "emailSendDate":
          return row.welcomeEmailSendDate == null
            ? <span className="pa-muted">Pending</span>
            : moment(row.welcomeEmailSendDate).format("YYYY-MM-DD");
        case "actions":
          return this.renderActions(row);
        default:
          return row[column.key];
      }
    };

    getPlanAdmins = () => {
      this.setState({ loading: true });
      axios
        .get("/api/PlanAdmin/getplanadmins?policyId=" + this.props.policy_id)
        .then((res) => {
          this.setState({
            planadmins: res.data,
            loading: false,
          });
        })
        .catch((err) => {
          toastr.error("Failed to get plan admins", "Error");
          this.setState({ loading: false });
        });
    }

    assignAdmin = async () => {
      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          policyId: this.props.policy_id,
          email: this.state.adminEmailInput,
        }),
      };

      try {
        const response = await fetch(
          "/api/assignplanadmin/assign",
          requestOptions
        );
        const data = await response.json();
        if(data.success) {
          this.getPlanAdmins();
          this.refreshLogs();
          toastr.success("Plan admin assigned","Success");
          this.setState({ adminEmailInput: "" });
        }
        else {
          toastr.error(data.message, "Error");
        }
      } catch (error) {
        toastr.error("Failed to assign admin", "Error");
      }
    };

    removeAdmin = (id:any) => {
      axios
      .post("/api/assignplanadmin/remove", {
        policyId: this.props.policy_id,
        userId: id,
        })
      .then((res) => {
        if(res.data.success) {
          this.getPlanAdmins();
          this.refreshLogs();
          toastr.success("Plan admin removed","Success");
        }
        else {
          toastr.error(res.data.message, "Error");
        }
      })
      .catch((err) => {
        toastr.error("Failed to remove admin", "Error");
      });
    };

    sendEmail = (id: any) => {
      axios
      .put("/api/assignplanadmin/sendpaemail", {
        policyId: this.props.policy_id,
        userId: id,
        })
      .then((res) => {
        if(res.data.success) {
          toastr.success("Email sent","Success");
          this.setState({
            isPAUpdated : true
          });
        }
        else {
          toastr.error(res.data.message, "Error");
        }
      })
      .catch((err) => {
        toastr.error("Failed to send email", "Error");
      });
    };

    render() {
      const {
        planadmins,
        loading,
        currentPage,
        itemsPerPage,
        adminEmailInput,
      } = this.state;
      const { policy_id } = this.props;

      const headers: PaColumn[] = [
        { label: "Last Name", key: "lastName" },
        { label: "First Name", key: "firstName" },
        { label: "Email", key: "email", className: "pa-col-email" },
        { label: "Hide on EE Portal", key: "hideOnEE", flag: true, className: "pa-col-flag" },
        { label: "Receive Email", key: "receiveEmail", flag: true, className: "pa-col-flag" },
        { label: "PA Type", key: "paType", className: "pa-col-patype" },
        { label: "2 Factor", key: "twoFactorEnabled", flag: true, className: "pa-col-flag" },
        { label: "Is Trained", key: "isTrained", flag: true, className: "pa-col-flag" },
        { label: "Permissions", key: "permissions", className: "pa-col-permissions" },
        { label: "Role", key: "role" },
        { label: "Email Send Date", key: "emailSendDate" },
        { label: "Actions", key: "actions", className: "pa-col-actions" }
      ];

      const actionsContent = (
        <div
          className="RT-DataTable__list-actions"
          style={{ paddingBottom: 6, display: 'flex', alignItems: 'center' }}
        >
          <input
            className="RT-TextBox__input"
            type="text"
            placeholder="Enter admin email"
            id="paSearch"
            value={adminEmailInput}
            onChange={(event) => {
              this.setState({ adminEmailInput: event.target.value });
            }}
            style={{ backgroundColor: "white", width: "auto", height: "26px", marginRight: "8px" }}
          />
          <button
            className="RT-Button RT-Button--primary"
            onClick={this.assignAdmin}
            disabled={!this.state.adminEmailInput.trim()}
            style={{ height: "26px", width: "80px", padding: "0px" }}
          >
            Assign
          </button>
          {loading && <Spinner animation="border" size="sm" variant="primary" style={{ marginLeft: '10px' }} />}
        </div>
      );

      let tableAreaContent;

      if (loading) {
        tableAreaContent = (
          <div className="pad-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <Spinner
              className="pad-spinner"
              animation="border"
              variant="primary"
            />
          </div>
        );
      } else if (planadmins.length === 0) {
        tableAreaContent = (
          <div className="pad-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <span style={{fontWeight: "bold"}}>No plan admins found.</span>
          </div>
        );
      } else {
        const indexOfLastRecord = currentPage * itemsPerPage;
        const indexOfFirstRecord = indexOfLastRecord - itemsPerPage;
        const currentRecords = planadmins.slice(indexOfFirstRecord, indexOfLastRecord);
        const nPages = Math.ceil(planadmins.length / itemsPerPage);

        const adminList = currentRecords.map((data) => {
          const key = data.id || data.email;
          return (
            <tr key={key}>
              {headers.map((column) => (
                <td key={column.key} className={column.className}>
                  {this.renderCell(column, data)}
                </td>
              ))}
            </tr>
          );
        });

        tableAreaContent = (
          <>
            <Table hover
              className="ktm-table pa-table"
              style={{
                backgroundColor: "white",
                overflow: "auto",
                width: "100%",
                marginBottom: 0,
              }}
            >
              <thead>
                <tr>
                  {headers.map((column) => (
                    <th key={column.key} className={column.className}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {adminList.length > 0 ? adminList : (
                  <tr>
                    <td colSpan={headers.length} style={{ textAlign: 'center' }}>
                      No plan admins to display for the current page.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            {nPages > 1 && (
              <div className="pagination-container" style={{display: "flex", justifyContent:"space-between", alignItems: "center", marginTop:"10px", marginBottom:"10px"}}>
                <div className="pagination-info">
                  <span>Showing {indexOfFirstRecord + 1} - {Math.min(indexOfLastRecord, planadmins.length)} of {planadmins.length} entries</span>
                </div>
                <CustomPagination
                  nPages={nPages}
                  currentPage={currentPage}
                  setCurrentPage={this.setCurrentPage}
                />
              </div>
            )}
          </>
        );
      }

      return (
        <div className="RT-Stackable-Container RT-Stackable-Container--embedded RT-Stackable-Container--padding-none RT-Stackable-Container--scrollable">
          <div className="RT-Stackable-Container__main">
            <div
              className="RT-Stackable-Container__body"
              style={{ display: "block" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  overflow: "unset", 
                }}
              >
                {actionsContent}
                {tableAreaContent}
                <PlanAdminLogs
                  policy_id={policy_id}
                  refreshKey={this.state.logRefreshKey}
                />
                {this.renderConfirmModal()}
              </div>
            </div>
          </div>
        </div>
      );
    }
  }
);